import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Star, HeartHandshake, MapPin } from "lucide-react";
import { formatRON, mediaUrl, localizedName, listingPriceParts } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";

const PLACEHOLDER = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=200";

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

export function metrics(l) {
  const s = l.specs || {};
  const d = l.dynamic_specs || {};
  return {
    power: s.motor_power_kw ?? d.motor_power_kw ?? null,
    weight: s.weight_kg ?? null,
    rpm: s.max_rpm ?? null,
    capacity: d.capacity_m3 ?? null,
    year: s.year ?? null,
  };
}

export function TrustChips({ l, size = "sm" }) {
  const { t } = useTranslation();
  const cls = size === "sm" ? "text-[10px] px-1.5 py-0.5 gap-0.5" : "text-[11px] px-2 py-1 gap-1";
  const items = [];
  if (l.vip) items.push({ k: "vip", icon: Star, label: t("ux.trust.priority"), c: "bg-amber-500 text-slate-900" });
  if (l.ai_verified_price) items.push({ k: "ai", icon: ShieldCheck, label: t("ux.trust.aiVerified"), c: "bg-slate-900 text-white" });
  if (l.is_social_impact) items.push({ k: "si", icon: HeartHandshake, label: t("ux.trust.endorsed"), c: "bg-emerald-600 text-white" });
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`trust-chips-${l.id}`}>
      {items.map((it) => (
        <span key={it.k} className={`inline-flex items-center font-bold uppercase tracking-wide ${cls} ${it.c}`}>
          <it.icon className={size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3"} /> {it.label}
        </span>
      ))}
    </div>
  );
}

export default function ListingMatrix({ listings, selectedIds = [], onToggleCompare, compareFull = false }) {
  const { t, i18n } = useTranslation();
  const NUM = "px-1.5 py-0.5 text-right text-slate-700 tabular-nums";
  return (
    <div className="border border-slate-200 w-full max-w-full overflow-hidden" data-testid="listings-matrix">
      <table className="table-fixed w-full text-xs border-collapse">
        <colgroup>
          {onToggleCompare && <col className="w-9" />}
          <col />
          <col className="w-24 hidden md:table-column" />
          <col className="w-12" />
          <col className="w-12" />
          <col className="w-14" />
          <col className="w-14" />
          <col className="w-12" />
          <col className="w-24" />
        </colgroup>
        <thead>
          <tr className="bg-slate-900 text-white text-[10px] uppercase tracking-[0.08em]">
            {onToggleCompare && <th className="px-1.5 py-0.5 text-center" title={t("ux.compare.label")}>±</th>}
            <th className="text-left font-bold px-1.5 py-0.5">{t("ux.matrix.name")}</th>
            <th className="text-left font-bold px-1.5 py-0.5 hidden md:table-cell">{t("ux.matrix.location")}</th>
            <th className="text-right font-bold px-1.5 py-0.5">{t("ux.matrix.power")}</th>
            <th className="text-right font-bold px-1.5 py-0.5 hidden sm:table-cell">{t("ux.matrix.capacity")}</th>
            <th className="text-right font-bold px-1.5 py-0.5 hidden lg:table-cell">{t("ux.matrix.weight")}</th>
            <th className="text-right font-bold px-1.5 py-0.5 hidden lg:table-cell">{t("ux.matrix.rpm")}</th>
            <th className="text-right font-bold px-1.5 py-0.5 hidden sm:table-cell">{t("ux.matrix.year")}</th>
            <th className="text-right font-bold px-1.5 py-0.5">{t("ux.matrix.price")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 font-mono">
          {listings.map((l) => {
            const m = metrics(l);
            const to = `/anunt/${l.id}`;
            const onReq = l.price_on_request || !l.price;
            const checked = selectedIds.includes(l.id);
            const disabled = !checked && compareFull;
            return (
              <tr key={l.id} className={`transition-colors align-middle ${checked ? "bg-amber-50" : "hover:bg-orange-50/50"}`} data-testid={`matrix-row-${l.id}`}>
                {onToggleCompare && (
                  <td className="px-1.5 py-0.5 text-center">
                    <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => onToggleCompare(l)} data-testid={`compare-check-${l.id}`} aria-label={t("ux.compare.label")} />
                  </td>
                )}
                <td className="px-1.5 py-0.5 overflow-hidden">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Link to={to} className="w-7 h-7 shrink-0 bg-slate-100 border border-slate-200 overflow-hidden hidden sm:block">
                      <img src={mediaUrl(l.image) || PLACEHOLDER} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-full h-full object-cover" loading="lazy" />
                    </Link>
                    <div className="min-w-0 flex-1">
                      <TrustChips l={l} />
                      <Link to={to} className="block font-sans font-bold text-slate-900 leading-tight hover:text-orange-600 truncate">{localizedName(l, i18n.language)}</Link>
                      {l.brand && <span className="font-sans text-[10px] uppercase tracking-wide text-orange-600 font-bold truncate block">{l.brand}</span>}
                    </div>
                  </div>
                </td>
                <td className="px-1.5 py-0.5 hidden md:table-cell text-slate-500 font-sans text-[11px] truncate">
                  <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{dash(l.location || l.county)}</span></span>
                </td>
                <td className={NUM}>{dash(m.power)}</td>
                <td className={`${NUM} hidden sm:table-cell`}>{dash(m.capacity)}</td>
                <td className={`${NUM} hidden lg:table-cell`}>{dash(m.weight)}</td>
                <td className={`${NUM} hidden lg:table-cell`}>{dash(m.rpm)}</td>
                <td className={`${NUM} hidden sm:table-cell`}>{dash(m.year)}</td>
                <td className="px-1.5 py-0.5 text-right font-sans font-black text-slate-900 truncate">{onReq ? t("pd.priceOnRequest") : listingPriceParts(l).main}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
